import React, { useEffect, useRef, useState } from "react";
import { ChevronDown, Tag, Check, X } from "lucide-react";
import clsx from "clsx";

/**
 * Custom category picker — chip-styled, keyboard navigable.
 *
 * Replaces the native <select> with a dense, on-brand dropdown that uses
 * mono uppercase chips for each option. Includes:
 *   • An "uncategorized" option (empty string value)
 *   • Inline create-on-the-fly when `allowCreate` + `onCreate` are passed
 *   • Keyboard nav (↑/↓/Enter/Esc) when focused
 *
 * Controlled component: pass `value` (string, "" = uncategorized) and `onChange`.
 *
 * Props:
 *   value          — current category name (string, "" = uncategorized)
 *   categories     — array of category names (strings)
 *   onChange(name) — fired on selection
 *   onCreate(name) — optional. If provided, shows a "+ NEW" row that creates
 *                    a category, awaits the result, then selects it.
 *   placeholder    — text shown when value is "" (default: "— UNCATEGORIZED —")
 *   testid         — root data-testid
 *   buttonClassName — extra classes for the closed-state button
 *   menuWidth      — px width of the popover (default 220)
 *   disabled       — disable open
 */
export default function CategorySelect({
  value = "",
  categories = [],
  onChange,
  onCreate,
  placeholder = "— UNCATEGORIZED —",
  testid = "category-select",
  buttonClassName = "",
  menuWidth = 220,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const [hoverIdx, setHoverIdx] = useState(0);
  const rootRef = useRef(null);
  const inputRef = useRef(null);

  // Close on outside click or Esc.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (!rootRef.current?.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") { setOpen(false); }
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      setFilter("");
      setHoverIdx(0);
      setTimeout(() => inputRef.current?.focus(), 20);
    }
  }, [open]);

  const filterLower = filter.trim().toLowerCase();
  const filteredCats = categories.filter((c) => !filterLower || c.toLowerCase().includes(filterLower));
  const exactMatch = categories.some((c) => c.toLowerCase() === filterLower);
  const showCreateRow = !!onCreate && filterLower.length > 0 && !exactMatch;

  // Build the navigable item list: [{kind:"uncat"|"cat"|"create", value, label}]
  const items = [
    { kind: "uncat", value: "", label: placeholder },
    ...filteredCats.map((c) => ({ kind: "cat", value: c, label: c })),
    ...(showCreateRow ? [{ kind: "create", value: filter.trim(), label: `+ NEW · "${filter.trim()}"` }] : []),
  ];

  const pick = async (item) => {
    if (item.kind === "create" && onCreate) {
      try {
        await onCreate(item.value);
        onChange && onChange(item.value);
      } catch {
        /* parent surfaces toast */
      }
    } else {
      onChange && onChange(item.value);
    }
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (!open) {
      if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHoverIdx((i) => Math.min(items.length - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHoverIdx((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = items[hoverIdx] || items[0];
      if (item) pick(item);
    }
  };

  const displayLabel = value ? value : placeholder;

  return (
    <div ref={rootRef} className="relative inline-block" data-testid={testid}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        onKeyDown={onKeyDown}
        className={clsx(
          "flex items-center gap-1.5 px-2 py-1 border bg-bg-base mono text-2xs uppercase tracking-widest transition-colors min-w-[120px] max-w-full",
          value ? "text-amber border-amber/60 hover:border-amber" : "text-ink-mute border-line hover:border-line-strong hover:text-ink",
          disabled && "opacity-40 cursor-not-allowed",
          buttonClassName
        )}
        data-testid={`${testid}-button`}
      >
        <Tag className="w-3 h-3 shrink-0" />
        <span className="truncate flex-1 text-left">{displayLabel}</span>
        <ChevronDown className={clsx("w-3 h-3 shrink-0 transition-transform", open && "rotate-180")} />
      </button>

      {open && (
        <div
          className="absolute z-[150] mt-1 left-0 bg-bg-elevated border border-amber shadow-[0_8px_32px_rgba(0,0,0,0.6)] py-1"
          style={{ width: menuWidth }}
          onKeyDown={onKeyDown}
          data-testid={`${testid}-menu`}
        >
          <div className="px-2 py-1 border-b border-line">
            <input
              ref={inputRef}
              value={filter}
              onChange={(e) => { setFilter(e.target.value); setHoverIdx(0); }}
              onKeyDown={onKeyDown}
              placeholder="filter…"
              className="w-full bg-bg-base border border-line text-ink mono text-2xs uppercase tracking-widest px-2 py-1 focus:border-amber outline-none"
              data-testid={`${testid}-filter`}
              autoComplete="off"
            />
          </div>
          <div className="max-h-[260px] overflow-y-auto py-0.5">
            {items.map((item, i) => {
              const isHover = i === hoverIdx;
              const isSelected = item.value === value && item.kind !== "create";
              const isCreate = item.kind === "create";
              return (
                <button
                  key={`${item.kind}-${item.value || "uncat"}-${i}`}
                  type="button"
                  onMouseEnter={() => setHoverIdx(i)}
                  onClick={() => pick(item)}
                  className={clsx(
                    "w-full flex items-center gap-2 px-2 py-1.5 text-left mono text-2xs uppercase tracking-widest transition-colors",
                    isHover ? "bg-amber-glow/20 text-amber" : "text-ink-mute hover:text-ink",
                    isSelected && "text-amber",
                    isCreate && "border-t border-line mt-0.5"
                  )}
                  data-testid={`${testid}-item-${item.value || "uncat"}`}
                >
                  {item.kind === "uncat" && <X className="w-3 h-3 opacity-50" />}
                  {item.kind === "cat" && (isSelected ? <Check className="w-3 h-3" /> : <Tag className="w-3 h-3 opacity-60" />)}
                  {item.kind === "create" && <span className="text-cyan">+</span>}
                  <span className="truncate flex-1">{item.label}</span>
                </button>
              );
            })}
            {items.length === 0 && (
              <div className="px-2 py-2 mono text-2xs uppercase tracking-widest text-ink-mute italic">— no match —</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
